/**
 * Utility functions per formattazione date
 */

/**
 * Formatta una data in formato italiano
 * @param {string|Date} date - Data da formattare
 * @param {string} format - Formato desiderato (default: 'DD/MM/YYYY')
 * @returns {string} Data formattata
 */
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';
    
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    
    switch (format) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'DD/MM/YYYY HH:mm':
        const hours = dateObj.getHours().toString().padStart(2, '0');
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      case 'YYYY-MM-DD':
        return dateObj.toISOString().split('T')[0];
      case 'relative':
        return getRelativeTime(dateObj);
      default:
        return `${day}/${month}/${year}`;
    }
  } catch (error) {
    console.error('Errore formattazione data:', error);
    return 'N/A';
  }
};

/**
 * Formatta una data per input HTML (YYYY-MM-DD)
 * @param {string|Date} date - Data da formattare
 * @returns {string} Data per input HTML
 */
export const formatDateInput = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Errore formattazione data input:', error);
    return '';
  }
};

/**
 * Converte una data da input HTML (YYYY-MM-DD) a Date object
 * @param {string} dateString - Data in formato YYYY-MM-DD
 * @returns {Date} Date object
 */
export const parseDateInput = (dateString) => {
  if (!dateString) return null;
  
  try {
    return new Date(dateString);
  } catch (error) {
    console.error('Errore parsing data input:', error);
    return null;
  }
};

/**
 * Calcola il tempo relativo (es. "2 ore fa", "ieri", "3 giorni fa")
 * @param {Date} date - Data di riferimento
 * @returns {string} Tempo relativo
 */
export const getRelativeTime = (date) => {
  if (!date) return 'N/A';
  
  try {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);
    
    if (diffSec < 60) {
      return 'ora';
    } else if (diffMin < 60) {
      return `${diffMin} minuto${diffMin !== 1 ? 'i' : ''} fa`;
    } else if (diffHour < 24) {
      return `${diffHour} ora${diffHour !== 1 ? 'e' : ''} fa`;
    } else if (diffDay < 7) {
      return `${diffDay} giorno${diffDay !== 1 ? 'i' : ''} fa`;
    } else if (diffMonth < 12) {
      return `${diffMonth} mese${diffMonth !== 1 ? 'i' : ''} fa`;
    } else {
      return `${diffYear} anno${diffYear !== 1 ? 'ni' : ''} fa`;
    }
  } catch (error) {
    console.error('Errore calcolo tempo relativo:', error);
    return 'N/A';
  }
};

/**
 * Verifica se una data è valida
 * @param {string|Date} date - Data da validare
 * @returns {boolean} True se valida
 */
export const isValidDate = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime());
  } catch (error) {
    return false;
  }
};

/**
 * Calcola l'età da una data di nascita
 * @param {string|Date} birthDate - Data di nascita
 * @returns {number} Età in anni
 */
export const calculateAge = (birthDate) => {
  if (!birthDate || !isValidDate(birthDate)) return null;
  
  try {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    console.error('Errore calcolo età:', error);
    return null;
  }
};

/**
 * Formatta l'età in modo leggibile
 * @param {string|Date} birthDate - Data di nascita
 * @returns {string} Età formattata
 */
export const formatAge = (birthDate) => {
  const age = calculateAge(birthDate);
  if (age === null) return 'N/A';
  
  if (age < 1) {
    const months = Math.floor((new Date() - new Date(birthDate)) / (1000 * 60 * 60 * 24 * 30));
    return `${months} mese${months !== 1 ? 'i' : ''}`;
  } else {
    return `${age} anno${age !== 1 ? 'ni' : ''}`;
  }
};

/**
 * Verifica se una persona è maggiorenne
 * @param {string|Date} birthDate - Data di nascita
 * @returns {boolean} True se maggiorenne
 */
export const isAdult = (birthDate) => {
  const age = calculateAge(birthDate);
  return age !== null && age >= 18;
};

/**
 * Ottieni il primo giorno del mese
 * @param {Date} date - Data di riferimento
 * @returns {Date} Primo giorno del mese
 */
export const getFirstDayOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

/**
 * Ottieni l'ultimo giorno del mese
 * @param {Date} date - Data di riferimento
 * @returns {Date} Ultimo giorno del mese
 */
export const getLastDayOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
};

/**
 * Formatta un range di date
 * @param {string|Date} startDate - Data di inizio
 * @param {string|Date} endDate - Data di fine
 * @returns {string} Range formattato
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  
  if (start === 'N/A' && end === 'N/A') return 'N/A';
  if (start === 'N/A') return `Fino al ${end}`;
  if (end === 'N/A') return `Dal ${start}`;
  if (start === end) return start;
  
  return `${start} - ${end}`;
};
